import { Box, ModalHeader, Typo, useModal } from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import { DynamicDataOptions } from '../../../clients/assets'
import { OptionalCover, Quote } from '../../../domain'
import { useQuoteData } from '../../../hooks/adjustments/quote'
import {
  tCovers,
  tFormats,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useAdjustments } from '../../../state'
import {
  getOptionalCoverLink,
  getOptionalCoversContentBaseUrl,
  useConfig,
} from '../../../state/configuration'
import { useProduct } from '../../product/ProductProvider'
import { HelpLinkIFrame } from '../links/HelpLinkIFrame'
import { WriskTrans } from '../trans'

export const useOptionalCoverModal = (quote: Quote) => {
  const { t } = useWriskTranslation()
  const { show } = useModal()
  const { scheme } = useProduct()

  const dynamicData = useOptionalCoverDynamicDataOptions(quote)
  const contentBaseUrl = useConfig(getOptionalCoversContentBaseUrl(scheme.schemeCode))

  return useCallback(
    (coverOption: OptionalCover) => {
      const link =
        coverOption.insurer &&
        getOptionalCoverLink(contentBaseUrl, quote.wordingVersion, {
          ...coverOption,
          insurer: coverOption.insurer,
        })

      const dynamicDataOptions = dynamicData(coverOption)

      return coverOption.available
        ? show({
            content: (
              <HelpLinkIFrame src={link} dynamicDataOptions={dynamicDataOptions} />
            ),
            variant: 'iframe',
          })
        : show({
            content: (
              <Box>
                <ModalHeader
                  header={t(tCovers(coverOption.coverOption, 'unavailable', 'header'))}
                />
                <Typo>
                  <WriskTrans
                    t={t}
                    i18nKey={tCovers(
                      coverOption.coverOption,
                      'unavailable',
                      'description',
                    )}
                  />
                </Typo>
              </Box>
            ),
          })
    },
    [show, t, contentBaseUrl],
  )
}

const useOptionalCoverDynamicDataOptions = (quote: Quote) => {
  const quoteData = useQuoteData(quote)
  const adjustments = useAdjustments()
  const { t } = useWriskTranslation()

  return useCallback(
    (coverOption: OptionalCover) => {
      const adjustmentData: Record<string, string> = Object.fromEntries(
        (coverOption.contextualHelp?.adjustments ?? []).map((adjustmentName) => [
          adjustmentName,
          '' + quoteData(adjustments.getAdjustment(adjustmentName)),
        ]),
      )
      const premium = t(tFormats('currency.long'), { amount: coverOption.premium })

      return {
        dynamicData: { premium, ...adjustmentData },
        dynamicDataSections: [
          coverOption.active ? 'remove' : 'add',
          ...(coverOption.contextualHelp?.showSections || []),
        ],
      } satisfies DynamicDataOptions
    },
    [quote],
  )
}
